import type { RatingCellOption } from '@risksmart-app/i18n/src/ratings';
import {
  getDeclaredCoordinates,
  getRatingByBandedLikelihoodAndImpact,
} from '@risksmart-app/i18n/src/ratings';

/** A matrix cell option from either rating source, with the colour it renders in. */
export interface RatingCellOptionWithColour extends RatingCellOption {
  label: string;
  value: number;
  color: string;
}

export interface RatingBandRating {
  label: string;
  value: number;
  color: string;
}

/**
 * `[from, to)` along one axis. The outermost bands of a scale are unbounded
 * (`-Infinity` / `Infinity`): the rule clamps anything beyond the scale into
 * them, and the renderer clips them to the plane's extent.
 */
export type AxisInterval = [number, number];

export interface PlaneExtent {
  likelihood: AxisInterval;
  impact: AxisInterval;
}

/**
 * One matrix cell as a region of the continuous plane: every pair inside it
 * bands to (`likelihood`, `impact`), and `rating` is what the shared rule
 * resolves there. `null` where that declared coordinate has no cell — the
 * gapped-matrix case, which must be drawn as unrated rather than take a
 * neighbour's colour.
 */
export interface RatingBandRegion {
  likelihood: number;
  impact: number;
  likelihoodRange: AxisInterval;
  impactRange: AxisInterval;
  rating: RatingBandRating | null;
}

export interface RatingBandPlane {
  regions: RatingBandRegion[];
  /** Undefined when no option declares a cell, so there is no scale to draw. */
  extent: PlaneExtent | undefined;
}

/**
 * The axis range a declared scale needs. It opens at the lowest declared
 * value and closes one step past the highest, so the top band — unbounded in
 * the rule — draws as wide as the step below it (one unit on a single-value
 * scale). Widened to take in any plotted value outside the scale, because the
 * rule clamps those into the end bands rather than dropping them.
 */
export const getAxisExtent = (
  declared: readonly number[],
  values: readonly number[]
): AxisInterval | undefined => {
  const lowest = declared[0];
  const highest = declared[declared.length - 1];
  if (lowest === undefined || highest === undefined) {
    return undefined;
  }
  const stepBelowHighest =
    declared.length > 1 ? highest - declared[declared.length - 2] : 1;

  return [
    Math.min(lowest, ...values),
    Math.max(highest + stepBelowHighest, ...values),
  ];
};

/**
 * Splits an axis at its declared values into `[from, to)` intervals. The first
 * and last are unbounded, which is how the rule's clamp below the lowest and
 * above the highest declared value appears on the plane.
 */
const intervalsAt = (declared: readonly number[]): AxisInterval[] =>
  declared.map((value, index) => [
    index === 0 ? -Infinity : value,
    index === declared.length - 1 ? Infinity : declared[index + 1],
  ]);

export const buildRatingBandRegions = (
  options: readonly RatingCellOptionWithColour[]
): RatingBandRegion[] => {
  const declared = getDeclaredCoordinates(options);
  const likelihoodIntervals = intervalsAt(declared.likelihoods);
  const impactIntervals = intervalsAt(declared.impacts);

  return declared.likelihoods.flatMap((likelihood, row) =>
    declared.impacts.map((impact, column): RatingBandRegion => {
      // A declared coordinate bands to itself, so this is the rule's verdict
      // for every pair the region contains — not a boundary computed here.
      const result = getRatingByBandedLikelihoodAndImpact(
        options,
        likelihood,
        impact
      );

      return {
        likelihood,
        impact,
        likelihoodRange: likelihoodIntervals[row],
        impactRange: impactIntervals[column],
        rating:
          result.status === 'resolved'
            ? {
                label: result.rating.label,
                value: result.rating.value,
                color: result.rating.color,
              }
            : null,
      };
    })
  );
};

/**
 * The background of a continuous likelihood × impact plane: the regions each
 * rating band covers, and the axis extents that fit both the scale and the
 * plotted values.
 */
export const buildRatingBandPlane = (
  options: readonly RatingCellOptionWithColour[],
  plotted: { likelihoods: readonly number[]; impacts: readonly number[] }
): RatingBandPlane => {
  const declared = getDeclaredCoordinates(options);
  const likelihood = getAxisExtent(declared.likelihoods, plotted.likelihoods);
  const impact = getAxisExtent(declared.impacts, plotted.impacts);
  if (!likelihood || !impact) {
    return { regions: [], extent: undefined };
  }

  return {
    regions: buildRatingBandRegions(options),
    extent: { likelihood, impact },
  };
};

const within = ([from, to]: AxisInterval, value: number): boolean =>
  value >= from && value < to;

/** The region a pair falls in; every pair of finite scores is in exactly one. */
export const findRegionContaining = (
  regions: readonly RatingBandRegion[],
  pair: { likelihood: number; impact: number }
): RatingBandRegion | undefined =>
  regions.find(
    (region) =>
      within(region.likelihoodRange, pair.likelihood) &&
      within(region.impactRange, pair.impact)
  );
